import { useQueryClient } from '@tanstack/react-query';
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from 'react';
import { useDispatch } from 'react-redux';

import { HeaderTagType } from '@payaca/components/headerTag/HeaderTag';
import DealListedScheduledEvents from '../dealListedScheduledEvents/DealListedScheduledEvents';

import { useCustomerForDeal, useDeal } from '@payaca/store/hooks/appState';
import * as scheduledEventActions from '@payaca/store/scheduledEvents/scheduledEventsActions';

import { ScheduledEvent } from '@payaca/types/scheduledEventsTypes';

import { CreateScheduledEventRequestData } from '@payaca/store/scheduledEvents/scheduledEventsTypes';
import { DeepPartial } from '@payaca/utilities/types';
import ScheduleContextProvider, {
  ScheduleContext,
} from '../contextProviders/ScheduleContextProvider';

import { getContactsToNotify } from '@payaca/helpers/scheduledEventsHelper';

import projectKeys from '@/api/queries/project/keyFactory';
import { useSelector } from '@/api/state';
import { useIsMounted } from '@/hooks/useIsMounted';
import { Deal } from '@payaca/types/dealTypes';
import useGetProjectByIdOverview from '../../../api/queries/project/useGetProjectByIdOverview';
import { ScheduleWithStickyControls } from '../scheduleDispatchMain/ScheduleWithStickyControls';
import DealScheduledEventsSkeleton from './DealScheduledEventsSkeleton';

interface Props {
  dealId: number;
  onDealUpdateSuccess: () => void;
}

const DealScheduledEvents: FC<Props> = ({
  dealId,
  onDealUpdateSuccess,
}: Props): JSX.Element => {
  const customer = useCustomerForDeal(dealId);
  const deal = useDeal(dealId);
  const currentUserId = useSelector((state: any) => state.users.myProfile.id);

  const initialScheduledEvent: DeepPartial<CreateScheduledEventRequestData> =
    useMemo(() => {
      const initialScheduledEvent: DeepPartial<CreateScheduledEventRequestData> =
        {
          dealId,
          name: customer?.name,
          userAssignments: [currentUserId],
        };
      const siteAddress = deal?.siteAddresses?.[0];
      if (siteAddress) {
        // default event to have first site address
        initialScheduledEvent.location = {
          id: siteAddress.address.id,
          address: siteAddress.address,
          contacts: siteAddress.contacts,
        };
      }

      initialScheduledEvent.contactsToNotify = getContactsToNotify({
        customer,
        locationContacts: siteAddress?.contacts,
      });

      return initialScheduledEvent;
      // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'dealId'
    }, [deal, customer, currentUserId]);

  return (
    <ScheduleContextProvider
      createEventBase={initialScheduledEvent}
      // Don't allow new events to be created on an archived deal
      allowEventCreation={!deal?.archivedAt}
      hideCreateEventButton
      hiddenFields={['customerIds']}
    >
      <ScheduleTabContent
        dealId={dealId}
        onDealUpdateSuccess={onDealUpdateSuccess}
      />
    </ScheduleContextProvider>
  );
};

export default DealScheduledEvents;

const ScheduleTabContent: FC<{
  onDealUpdateSuccess: () => void;
  dealId: Deal['id'];
}> = ({ onDealUpdateSuccess, dealId }) => {
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const queryClient = useQueryClient();
  const { project } = useGetProjectByIdOverview(dealId.toString());
  const { listenerSubscribe, listenerUnsubscribe } =
    useContext(ScheduleContext);

  const [isGetting, setIsGetting] = useState(false);

  const getDealScheduledEvents = useCallback(() => {
    setIsGetting(true);
    dispatch(
      scheduledEventActions.requestGetListedScheduledEvents(
        {
          dealId,
          pageSize: 50,
        },
        (events) => {
          if (!isMounted.current) return;
          setDealScheduledEvents(events);
          setIsGetting(false);
        }
      )
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'dealId', 'isMounted.current', and 'dispatch'
  }, []);

  useEffect(() => {
    getDealScheduledEvents();

    const refetch = () => {
      if (project?.publicId) {
        void queryClient.invalidateQueries({
          queryKey: projectKeys.profitBreakdown(project?.publicId),
        });
      }
      getDealScheduledEvents();
    };

    listenerSubscribe('onEventCreated', 'scheduleTabContent', refetch);

    listenerSubscribe('onEventUpdated', 'scheduleTabContent', refetch);

    listenerSubscribe('onEventDeleted', 'scheduleTabContent', refetch);

    return () => {
      listenerUnsubscribe('onEventCreated', 'scheduleTabContent');
      listenerUnsubscribe('onEventUpdated', 'scheduleTabContent');
      listenerUnsubscribe('onEventDeleted', 'scheduleTabContent');
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'queryClient', 'listenerUnsubscribe', 'project.publicId', 'listenerSubscribe', and 'getDealScheduledEvents'
  }, []);

  const [dealScheduledEvents, setDealScheduledEvents] = useState<
    ScheduledEvent[]
  >([]);

  return (
    <div className="@container/scheduletab">
      <div className="@lg/scheduletab:flex-row flex flex-col gap-8 ">
        <div className="@lg/scheduletab:basis-60 shrink-0 grow-0">
          {/* @lg/scheduletab:basis-80  */}
          {!dealScheduledEvents.length && isGetting && (
            <DealScheduledEventsSkeleton />
          )}
          {!!dealScheduledEvents?.length && (
            <DealListedScheduledEvents
              titleHeaderTagType={HeaderTagType.H3}
              dealScheduledEvents={dealScheduledEvents}
              onDealUpdateSuccess={onDealUpdateSuccess}
              hideTitles={true}
            />
          )}
          {!dealScheduledEvents?.length && !isGetting && (
            <>
              <h3>No events</h3>
              <p>This project {"doesn't"} have any events yet</p>
            </>
          )}
        </div>
        <ScheduleWithStickyControls />
      </div>
    </div>
  );
};
